import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    CircularProgress,
    Chip,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Delete as DeleteIcon,
    Description as FileIcon,
    Refresh as RefreshIcon,
    Visibility as EyeIcon,
    CheckCircle as CompletedIcon,
    Error as ErrorIcon,
    Pending as PendingIcon,
    Sync as ProcessingIcon,
    Download as DownloadIcon
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { apiClient } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import DocumentUploadDialog from '@/components/upload/DocumentUploadDialog';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface Document {
    id: number;
    document_name: string;
    status: string;
    uploaded_at: string;
    file_size: number;
    // MD转换相关字段
    processing_status?: string;  // pending, processing, completed, failed
    processing_progress?: number;  // 0-100
    processing_error?: string;
    markdown_path?: string;  // 数据库中存储的实际markdown文件路径
    processed_at?: string;
}

const DocumentPanel: React.FC = () => {
    const { projectId: projectIdStr } = useParams<{ projectId: string }>();
    const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
    const { user } = useAuth(); // 获取当前用户信息
    const [documents, setDocuments] = useState<Document[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    // Markdown查看模态框状态
    const [markdownModalOpen, setMarkdownModalOpen] = useState(false);
    const [markdownContent, setMarkdownContent] = useState<string>('');
    const [markdownTitle, setMarkdownTitle] = useState<string>('');
    const [loadingMarkdown, setLoadingMarkdown] = useState(false);

    // 判断用户是否有管理员权限
    const isAdmin = user?.role === 'super_admin';
  
    const [kgStatus, setKgStatus] = useState<{
        status: string;
        progress: number;
        total_documents: number;
        converted_documents: number;
        built_documents: number;
        conversion_progress: number;
        build_progress: number;
        processing_status?: {
            converted: number;
            processing: number;
            failed: number;
            pending: number;
        };
        cognee_status?: {
            completed: number;
            processing: number;
            failed: number;
            pending: number;
        };
    } | null>(null);

    const fetchDocuments = async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const response = await apiClient.documents.getList(projectId.toString());
            if (response.success) {
                setDocuments(response.data?.documents || []);
            } else {
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const fetchKgStatus = async () => {
        if (!projectId) return;
        try {
            const response = await apiClient.knowledgeGraph.getStatus(projectId.toString());
            if (response.success) {
                setKgStatus(response.data);
                // 发送知识图谱状态更新事件，供其他组件使用
                window.dispatchEvent(new CustomEvent('kgStatusUpdated', { detail: response.data }));
            }
        } catch (error) {
        }
    };

    const fetchTaskStatus = async () => {
        if (!projectId) return;
        try {
            const response = await apiClient.documentTasks.getStatus(projectId.toString());
            if (response.success) {
                // 更新文档的processing状态
                setDocuments(prev => prev.map(doc => {
                    const task = response.data.tasks?.find((t: any) =>
                        t.task_type === 'document_processing' &&
                        t.document_id === doc.id  // 修复：直接使用document_id字段
                    );
                    if (task) {
                        return {
                            ...doc,
                            processing_status: task.status,
                            processing_progress: task.progress,
                            processing_error: task.error_message
                        };
                    }
                    return doc;
                }));
            }
        } catch (error) {
        }
    };

    useEffect(() => {
        fetchDocuments();
        fetchKgStatus();
        fetchTaskStatus();

        // Poll for status if processing
        const interval = setInterval(() => {
            fetchKgStatus();
            fetchTaskStatus();
        }, 5000);

        // 监听全局文档刷新事件
        const handleGlobalRefresh = () => {
            fetchDocuments();
            fetchKgStatus();
            fetchTaskStatus();
        };

        window.addEventListener('documentsShouldRefresh', handleGlobalRefresh);

        return () => {
            clearInterval(interval);
            window.removeEventListener('documentsShouldRefresh', handleGlobalRefresh);
        };
    }, [projectId]);

    const handleDelete = async (id: number) => {
        if (!projectId) return;
        if (!window.confirm('确定要删除此文档吗？')) return;

        try {
            await apiClient.documents.delete(projectId.toString(), id.toString());
            fetchDocuments();
            fetchKgStatus(); // Update status after deletion
        } catch (error) {
        }
    };

    const handleRebuild = async () => {
        if (!projectId) return;
        try {
            const response = await apiClient.knowledgeGraph.rebuild(projectId.toString(), false);

            // 处理新的响应格式
            if (response.data) {
                const { successful_count, failed_count, created_tasks, failed_tasks } = response.data;

                if (response.success) {
                    // 成功创建任务

                    // 可以显示更详细的信息给用户
                    if (successful_count > 0) {
                        // 成功创建的任务信息
                    }

                    if (failed_count > 0) {
                        // 失败的任务信息
                    }
                } else {
                    // 有任务冲突或其他问题

                    // 如果有进行中的任务，可以显示给用户
                    if (response.data?.existing_tasks) {
                    }
                }
            }

            // 立即刷新知识图谱状态，避免等待5秒轮询
            fetchKgStatus();
            // 额外延迟1秒再刷新一次，确保任务状态已更新
            setTimeout(() => {
                fetchKgStatus();
            }, 1000);
        } catch (error) {
        }
    };

    const handleForceRebuild = async () => {
        if (!projectId) return;

        // 确认对话框
        const confirmed = window.confirm(
            '确定要强制重新构建所有已转换的文档吗？\n\n' +
            '这将会：\n' +
            '• 重新处理所有已完成转换的文档（包括已经构建过的文档）\n' +
            '• 可能需要较长时间\n' +
            '• 建议仅在需要更新知识图谱时使用'
        );

        if (!confirmed) return;

        try {
            const response = await apiClient.knowledgeGraph.rebuild(projectId.toString(), true);

            // 处理新的响应格式
            if (response.data) {
                const { successful_count, failed_count, created_tasks, failed_tasks } = response.data;

                if (response.success) {
                    // 成功创建任务

                    if (successful_count > 0) {
                        alert(`已创建 ${successful_count} 个文档的重新构建任务`);
                    }

                    if (failed_count > 0) {
                    }
                } else {
                    if (response.data?.existing_tasks) {
                    }
                    alert(response.message || '创建任务失败');
                }
            }

            // 立即刷新知识图谱状态
            fetchKgStatus();
            setTimeout(() => {
                fetchKgStatus();
            }, 1000);
        } catch (error) {
            alert('强制重建失败，请稍后重试');
        }
    };

    // 下载文档
    const handleDownloadDocument = async (filename: string) => {
        if (!projectId) return;
        try {
            const response = await apiClient.documents.download(projectId.toString(), filename);

            // 创建blob URL用于下载
            const blobUrl = URL.createObjectURL(response);

            // 创建下载链接
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename; // 使用原始文件名作为下载文件名
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // 清理blob URL以避免内存泄漏
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (error) {
            alert('无法下载文档，请稍后重试');
        }
    };

    // 查看Markdown文件
    const handleViewMarkdown = async (document: Document) => {
        if (!projectId || !document.markdown_path) {
            alert('Markdown文件不存在或路径未设置');
            return;
        }

        setLoadingMarkdown(true);
        try {
            // 从完整路径中提取文件名
            const filename = document.markdown_path.split('/').pop() || document.markdown_path;

            const response = await apiClient.documents.getMarkdown(projectId.toString(), filename);

            if (response.success) {

                // 设置模态框状态
                setMarkdownTitle(document.document_name);
                setMarkdownContent(response.data);
                setMarkdownModalOpen(true);
            } else {
                throw new Error(response.message);
            }
        } catch (error) {
            alert('无法查看Markdown文件，请稍后重试');
        } finally {
            setLoadingMarkdown(false);
        }
    };


    
    const getProcessingStatusColor = (status?: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'running': return 'warning';  // 后端使用'running'状态
            case 'processing': return 'warning';  // 兼容处理
            case 'failed': return 'error';
            case 'pending': return 'info';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    const getProcessingStatusText = (status?: string) => {
        switch (status) {
            case 'pending': return '等待中';
            case 'running': return '转换中';  // 后端使用'running'状态
            case 'processing': return '转换中';  // 兼容处理
            case 'completed': return '已完成';
            case 'failed': return '转换失败';
            case 'cancelled': return '已取消';
            default: return '未开始';
        }
    };

    const getKgStatusText = (status?: string) => {
        switch (status) {
            case 'not_started': return '未开始';
            case 'pending_update': return '待更新';
            case 'building': return '构建中';
            case 'completed': return '已完成';
            case 'failed': return '构建失败';
            case 'idle': return '未开始';  // 兼容旧状态
            case 'processing': return '处理中';  // 兼容旧状态
            default: return status || '未知';
        }
    };
  
    return (
        <>
            {/* 添加脉冲动画样式 */}
            <style>
                {`
                    @keyframes pulse {
                        0% {
                            transform: scale(1);
                            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        }
                        50% {
                            transform: scale(1.1);
                            box-shadow: 0 2px 8px rgba(255,152,0,0.4);
                        }
                        100% {
                            transform: scale(1);
                            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                        }
                    }
                `}
            </style>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'white' }}>
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>调研来源</Typography>
                <Box>
                    <Tooltip title="刷新">
                        <IconButton size="small" onClick={() => { fetchDocuments(); fetchKgStatus(); }}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="上传文档">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<UploadIcon />}
                            onClick={() => setUploadDialogOpen(true)}
                            sx={{ ml: 1 }}
                        >
                            上传文档
                        </Button>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : documents.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        <Typography variant="body2">暂无文档</Typography>
                        <Button
                            startIcon={<UploadIcon />}
                            sx={{ mt: 1 }}
                            onClick={() => setUploadDialogOpen(true)}
                        >
                            上传文档
                        </Button>
                    </Box>
                ) : (
                    <List>
                        {documents.map((doc) => (
                            <ListItem
                                key={doc.id}
                                secondaryAction={
                                    <Box>
                                        {doc.processing_status === 'completed' && doc.markdown_path && (
                                            <Tooltip title="查看Markdown文件">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewMarkdown(doc)}
                                                >
                                                    <EyeIcon fontSize="small" color="success" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton
                                            size="small"
                                            title="下载文档"
                                            onClick={() => handleDownloadDocument(doc.document_name)}
                                        >
                                            <DownloadIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton edge="end" size="small" onClick={() => handleDelete(doc.id)} title="删除">
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </Box>
                                }
                                sx={{ borderBottom: 1, borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}
                            >
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FileIcon fontSize="small" color="action" />
                                            <Typography variant="body2" noWrap title={doc.document_name} sx={{ fontWeight: 500 }}>
                                                {doc.document_name}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box sx={{ mt: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {(doc.file_size / 1024).toFixed(1)} KB
                                                </Typography>
                                            </Box>
                                            {/* MD转换状态 */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {doc.processing_status === 'running' && <ProcessingIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'processing' && <ProcessingIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'completed' && <CompletedIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'failed' && <ErrorIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'pending' && <PendingIcon sx={{ fontSize: 12 }} />}
                                                            {doc.processing_status === 'cancelled' && <PendingIcon sx={{ fontSize: 12 }} />}
                                                            {getProcessingStatusText(doc.processing_status)}
                                                        </Box>
                                                    }
                                                    size="small"
                                                    color={getProcessingStatusColor(doc.processing_status) as any}
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                                {(doc.processing_status === 'processing' || doc.processing_status === 'running') && doc.processing_progress && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {doc.processing_progress}%
                                                    </Typography>
                                                )}
                                                {doc.processing_status === 'failed' && doc.processing_error && (
                                                    <Tooltip title={doc.processing_error}>
                                                        <ErrorIcon fontSize="small" color="error" sx={{ fontSize: 14 }} />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>

            {/* Knowledge Graph Construction Status */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2, bgcolor: 'background.paper' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    知识图谱构建状态
                </Typography>

                {/* 状态信息和进度 */}
                <Box sx={{ mb: 2 }}>
                    {kgStatus ? (
                        <Box>
                            {/* 主要状态显示 */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {kgStatus.status === 'building' && <ProcessingIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                                    {kgStatus.status === 'pending_update' && <PendingIcon sx={{ fontSize: 16, color: 'warning.main' }} />}
                                    {kgStatus.status === 'completed' && <CompletedIcon sx={{ fontSize: 16, color: 'success.main' }} />}
                                    {kgStatus.status === 'failed' && <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                                    {kgStatus.status === 'not_started' && <PendingIcon sx={{ fontSize: 16, color: 'default.main' }} />}
                                    {(kgStatus.status === 'processing' || kgStatus.status === 'idle') && <PendingIcon sx={{ fontSize: 16, color: 'info.main' }} />}
                                    <Chip
                                        label={getKgStatusText(kgStatus.status)}
                                        size="small"
                                        color={
                                            kgStatus.status === 'building' ? 'info' :
                                                kgStatus.status === 'pending_update' ? 'warning' :
                                                    kgStatus.status === 'completed' ? 'success' :
                                                        kgStatus.status === 'failed' ? 'error' :
                                                            kgStatus.status === 'not_started' ? 'default' :
                                                                // 兼容旧状态
                                                                kgStatus.status === 'processing' ? 'warning' : 'info'
                                        }
                                        sx={{ height: 22, fontSize: '0.7rem' }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {/* <Button
                                        size="small"
                                        variant="outlined"
                                        sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                                        disabled={kgStatus.status === 'building'}
                                        onClick={handleGraphVisualization}
                                    >
                                        预览
                                    </Button> */}
                                    <Box sx={{ position: 'relative' }}>
                                        {/* 气泡框提醒 - 仅在待更新状态时显示 */}
                                        {kgStatus.status === 'pending_update' && (
                                            <>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -8,
                                                        left: -8,
                                                        zIndex: 10,
                                                        backgroundColor: 'warning.main',
                                                        color: 'white',
                                                        borderRadius: '50%',
                                                        width: 20,
                                                        height: 20,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '10px',
                                                        fontWeight: 'bold',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        animation: 'pulse 2s infinite',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    !
                                                </Box>
                                                {/* 提醒文字直接显示在左上角 */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: -60,
                                                        right: -10,
                                                        backgroundColor: 'white',
                                                        color: '#333',
                                                        padding: '6px 10px',
                                                        borderRadius: '6px',
                                                        fontSize: '11px',
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 10,
                                                        animation: 'pulse 2s infinite',
                                                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                        border: '1px solid #e0e0e0',
                                                        lineHeight: '1.4',
                                                        textAlign: 'center',
                                                        minWidth: '120px'
                                                    }}
                                                >
                                                    <Box sx={{ fontSize: '11px', fontWeight: 500, color: '#f57c00' }}>
                                                        知识图谱待更新
                                                    </Box>
                                                    <Box sx={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
                                                        点击构建
                                                    </Box>
                                                    {/* 指向按钮的三角形 */}
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -6,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '6px solid transparent',
                                                            borderRight: '6px solid transparent',
                                                            borderTop: '6px solid white',
                                                            zIndex: 11
                                                        }}
                                                    />
                                                    {/* 三角形边框 */}
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            bottom: -7,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '7px solid transparent',
                                                            borderRight: '7px solid transparent',
                                                            borderTop: '7px solid #e0e0e0',
                                                            zIndex: 10
                                                        }}
                                                    />
                                                </Box>
                                            </>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                                                onClick={handleRebuild}
                                                disabled={kgStatus.status === 'building'}
                                            >
                                                构建图谱
                                            </Button>
                                            {isAdmin && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                                                    onClick={handleForceRebuild}
                                                    disabled={kgStatus.status === 'building'}
                                                >
                                                    重新构建
                                                </Button>
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* 详细信息 */}
                            <Box sx={{ mb: 1, display: 'block' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                    文档总数: {kgStatus.total_documents || 0} |
                                    转换完成: <span style={{ color: '#4caf50' }}>{kgStatus.converted_documents || 0}</span> |
                                    构建完成: <span style={{ color: kgStatus.built_documents === kgStatus.converted_documents && kgStatus.converted_documents > 0 ? '#4caf50' : '#ff9800' }}>
                                        {kgStatus.built_documents || 0}
                                    </span>
                                </Typography>

                                {kgStatus.status === 'building' && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                        转换进度: {kgStatus.conversion_progress || 0}% |
                                        构建进度: {kgStatus.build_progress || 0}%
                                    </Typography>
                                )}

                                {kgStatus.status === 'completed' && (
                                    <Typography variant="caption" color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                                        ✅ 知识图谱构建完成！
                                    </Typography>
                                )}

                                {kgStatus.cognee_status && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
                                        详细状态:
                                        <span style={{ color: '#4caf50', marginLeft: '4px' }}>完成{kgStatus.cognee_status.completed}</span> |
                                        <span style={{ color: '#ff9800', marginLeft: '4px' }}>处理中{kgStatus.cognee_status.processing}</span> |
                                        <span style={{ color: '#f44336', marginLeft: '4px' }}>失败{kgStatus.cognee_status.failed}</span> |
                                        <span style={{ color: '#9e9e9e', marginLeft: '4px' }}>待处理{kgStatus.cognee_status.pending}</span>
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} />
                            <Typography variant="caption" color="text.secondary">
                                加载中...
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* 进度条 */}
                <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            总体进度
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {kgStatus?.build_progress || 0}% (构建)
                        </Typography>
                    </Box>
                    <Box sx={{ width: '100%', bgcolor: 'action.hover', borderRadius: 1, height: 8 }}>
                        <Box
                            sx={{
                                width: `${kgStatus?.build_progress || 0}%`,
                                bgcolor: kgStatus?.status === 'building' ? 'info.main' :
                                    kgStatus?.status === 'pending_update' ? 'warning.main' :
                                        kgStatus?.status === 'completed' ? 'success.main' :
                                            kgStatus?.status === 'failed' ? 'error.main' :
                                                kgStatus?.status === 'not_started' ? 'grey.400' :
                                                    // 兼容旧状态
                                                    kgStatus?.status === 'processing' ? 'warning.main' : 'grey.400',
                                borderRadius: 1,
                                height: 8,
                                transition: 'width 0.3s ease'
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Markdown查看模态框 */}
            <Dialog
                open={markdownModalOpen}
                onClose={() => setMarkdownModalOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        height: '80vh',
                        maxHeight: '800px'
                    }
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" component="div">
                        {markdownTitle}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {loadingMarkdown ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '300px',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            <CircularProgress size={48} />
                            <Typography variant="body1" color="text.secondary">
                                正在加载Markdown文件...
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{
                            height: '100%',
                            overflow: 'auto',
                            p: 3,
                            backgroundColor: '#fafafa'
                        }}>
                            <Box
                                sx={{
                                    backgroundColor: '#fff',
                                    borderRadius: 1,
                                    p: 3,
                                    boxShadow: 1,
                                    '& .markdown-content': {
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
                                        fontSize: '16px',
                                        lineHeight: 1.6,
                                        color: '#333',
                                        '& h1': {
                                            fontSize: '2rem',
                                            fontWeight: 'bold',
                                            marginBottom: '1rem',
                                            color: '#1976d2',
                                            borderBottom: '2px solid #e3f2fd',
                                            paddingBottom: '0.5rem'
                                        },
                                        '& h2': {
                                            fontSize: '1.5rem',
                                            fontWeight: 'bold',
                                            marginBottom: '0.75rem',
                                            color: '#1976d2',
                                            marginTop: '2rem'
                                        },
                                        '& h3': {
                                            fontSize: '1.25rem',
                                            fontWeight: 'bold',
                                            marginBottom: '0.5rem',
                                            color: '#424242',
                                            marginTop: '1.5rem'
                                        },
                                        '& p': {
                                            marginBottom: '1rem',
                                            lineHeight: 1.6
                                        },
                                        '& ul, & ol': {
                                            marginBottom: '1rem',
                                            paddingLeft: '1.5rem'
                                        },
                                        '& li': {
                                            marginBottom: '0.25rem',
                                            lineHeight: 1.5
                                        },
                                        '& blockquote': {
                                            borderLeft: '4px solid #1976d2',
                                            paddingLeft: '1rem',
                                            paddingTop: '0.5rem',
                                            paddingBottom: '0.5rem',
                                            marginTop: '1rem',
                                            marginBottom: '1rem',
                                            backgroundColor: '#f5f5f5',
                                            fontStyle: 'italic'
                                        },
                                        '& code': {
                                            backgroundColor: '#f5f5f5',
                                            padding: '2px 4px',
                                            borderRadius: '4px',
                                            fontSize: '0.875em',
                                            fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace'
                                        },
                                        '& pre': {
                                            backgroundColor: '#263238',
                                            color: '#fff',
                                            padding: '1rem',
                                            borderRadius: '4px',
                                            overflowX: 'auto',
                                            marginBottom: '1rem',
                                            '& code': {
                                                backgroundColor: 'transparent',
                                                padding: 0
                                            }
                                        },
                                        '& table': {
                                            width: '100%',
                                            borderCollapse: 'collapse',
                                            marginBottom: '1rem'
                                        },
                                        '& th, & td': {
                                            border: '1px solid #e0e0e0',
                                            padding: '8px 12px',
                                            textAlign: 'left'
                                        },
                                        '& th': {
                                            backgroundColor: '#f5f5f5',
                                            fontWeight: 'bold'
                                        },
                                        '& a': {
                                            color: '#1976d2',
                                            textDecoration: 'none',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        },
                                        '& img': {
                                            maxWidth: '100%',
                                            height: 'auto',
                                            borderRadius: '4px',
                                            marginTop: '1rem',
                                            marginBottom: '1rem'
                                        }
                                    }
                                }}
                                className="markdown-content"
                            >
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw]}
                                >
                                    {markdownContent}
                                </ReactMarkdown>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setMarkdownModalOpen(false)}
                        variant="outlined"
                    >
                        关闭
                    </Button>
                </DialogActions>
            </Dialog>

            <DocumentUploadDialog
                open={uploadDialogOpen}
                onClose={() => {
                    setUploadDialogOpen(false);
                    // 关闭弹窗时也刷新文档列表，以防有上传中的文件或新文件
                    fetchDocuments();
                    fetchKgStatus();
                }}
                onUploadComplete={() => {
                    setUploadDialogOpen(false);
                    fetchDocuments();
                    fetchKgStatus();
                    // 触发全局文档刷新事件，通知其他页面更新文档列表
                    window.dispatchEvent(new CustomEvent('documentsShouldRefresh'));
                }}
                projectId={projectId ? projectId.toString() : ''}
            />
        </Box>
        </>
    );
};

export default DocumentPanel;
